import type { MenuRow } from '@jane/business-admin/types';
/* eslint-disable-next-line no-restricted-imports */
import type { SpecialSchedule } from '@jane/shared/models';

import {
  convert12HourTimeTo24HourTime,
  convert24HourTimeTo12HourTime,
} from '../../../../shared/schedule/ScheduleTimeValidation';

const DAYS = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
] as const;

export const DEFAULT_ROW_SCHEDULE = {
  enabled_sunday: true,
  enabled_monday: true,
  enabled_tuesday: true,
  enabled_wednesday: true,
  enabled_thursday: true,
  enabled_friday: true,
  enabled_saturday: true,
  enabled_sunday_all_day: true,
  enabled_monday_all_day: true,
  enabled_tuesday_all_day: true,
  enabled_wednesday_all_day: true,
  enabled_thursday_all_day: true,
  enabled_friday_all_day: true,
  enabled_saturday_all_day: true,
  end_time_sunday: null,
  start_time_sunday: null,
  end_time_monday: null,
  start_time_monday: null,
  end_time_tuesday: null,
  start_time_tuesday: null,
  end_time_wednesday: null,
  start_time_wednesday: null,
  end_time_thursday: null,
  start_time_thursday: null,
  end_time_friday: null,
  start_time_friday: null,
  end_time_saturday: null,
  start_time_saturday: null,
};

export const formatSchedule = (schedule: SpecialSchedule) => {
  const formatted = Object.entries(schedule).reduce((acc, [key, value]) => {
    if (key.startsWith('start_time') || key.startsWith('end_time')) {
      acc[key] = convert12HourTimeTo24HourTime(value);
    } else {
      acc[key] = value;
    }

    return acc;
  }, {} as any);

  return formatted;
};

const hasScheduleOverrides = (
  schedule: SpecialSchedule,
  isCreateMode?: boolean
) => {
  if (isCreateMode) {
    return false;
  }

  const allDayEveryDay = DAYS.every(
    (day) =>
      schedule[`enabled_${day}` as keyof SpecialSchedule] &&
      schedule[`enabled_${day}_all_day` as keyof SpecialSchedule]
  );

  const anyScheduleOverrides = DAYS.some(
    (day) =>
      schedule[`start_time_${day}` as keyof SpecialSchedule] ||
      schedule[`end_time_${day}` as keyof SpecialSchedule]
  );

  return !allDayEveryDay || anyScheduleOverrides;
};

const buildDayEntries = (rowSchedule: SpecialSchedule) =>
  DAYS.reduce(
    (acc, day) => ({
      ...acc,
      [`enabled_${day}`]: rowSchedule[`enabled_${day}`] || false,
      [`enabled_${day}_all_day`]:
        rowSchedule[`enabled_${day}_all_day`] ||
        (!rowSchedule[`end_time_${day}`] &&
          !rowSchedule[`start_time_${day}`]) ||
        false,
      [`start_time_${day}`]:
        convert24HourTimeTo12HourTime(rowSchedule[`start_time_${day}`]) || '',
      [`end_time_${day}`]:
        convert24HourTimeTo12HourTime(rowSchedule[`end_time_${day}`]) || '',
    }),
    {} as Record<string, boolean | string>
  );

export const parseSchedule = (row?: MenuRow, isCreateMode?: boolean) => {
  if (isCreateMode || !row?.schedule) {
    const schedule = {
      ...DAYS.reduce(
        (acc, day) => ({
          ...acc,
          [`enabled_${day}`]: true,
          [`enabled_${day}_all_day`]: true,
          [`start_time_${day}`]: '',
          [`end_time_${day}`]: '',
        }),
        {} as Record<string, boolean | string>
      ),
      use_store_close_time_for_end: false,
      enabled_date_end_checked: false,
      enabled_date_start_checked: false,
      enabled_date_end: '',
      enabled_date_start: '',
      end_date: '',
      end_time: convert24HourTimeTo12HourTime('00:00'),
      start_date: '',
      start_time: convert24HourTimeTo12HourTime('00:00'),
      use_store_close_time: false,
    };
    return {
      schedule,
      has_schedule_overrides: hasScheduleOverrides(schedule, true),
    };
  }

  const { schedule: rowSchedule } = row;

  const fullEndDate = rowSchedule.end_time
    ? rowSchedule.end_time.split(' ')
    : [];
  const fullStartDate = rowSchedule.start_time
    ? rowSchedule.start_time.split(' ')
    : [];
  const [endDate, endTime] = fullEndDate;
  const [startDate, startTime] = fullStartDate;

  const schedule = {
    ...buildDayEntries(rowSchedule),
    use_store_close_time_for_end:
      rowSchedule.use_store_close_time_for_end || false,
  };
  return {
    schedule,
    has_schedule_overrides: hasScheduleOverrides(schedule, false),
    enabled_date_start_checked: fullStartDate.filter(Boolean).length > 1,
    enabled_date_end_checked: fullEndDate.filter(Boolean).length > 1,
    end_date: endDate,
    end_time: convert24HourTimeTo12HourTime(endTime),
    start_date: startDate,
    start_time: convert24HourTimeTo12HourTime(startTime),
    enabled_date_end: '',
    enabled_date_start: '',
    use_store_close_time: rowSchedule.use_store_close_time_for_end || false,
  };
};
